import { DATASET_INITIAL_ID } from 'common/constants';
import { CommonFilterList, FilterConfig, FilterParent } from 'common/models/filter.model';
import { selectFilterParent, setFilterParent } from 'common/store/slices/filter-parent.slice';
import {
  ALIGNMENT_DETAIL_FILTER,
  ALIGNMENT_DETAIL_SEGMENT_FILTER,
  ALIGNMENT_FILTER,
  ALIGNMENT_SUMMARY_SEGMENT_FILTER,
  CLIENT_FILTER_EXCLUSIONS,
  COACH_FILTER,
  COACH_SEGMENT_FILTER,
  COACH_TEAM_MEMBER_DETAILS_FILTER,
} from 'modules/insights/constants';
import { ELSIE_AI_FILTER } from 'modules/insights/constants/constants-elsie-ai-filters';
import {
  INSIGHT_DETAIL_FILTER,
  INSIGHT_SEGMENT_FILTER,
  INSIGHT_SUMMARY_FILTER,
  INSIGHT_COMPARE_FILTER,
} from 'modules/insights/constants/constants-insights-filters';
import {
  INTERACTION_FILTER,
  INTERACTION_SEGMENT_FILTER,
} from 'modules/insights/constants/constants-interaction-filters';
import { MIR_FILTER } from 'modules/insights/constants/constants-mir-filters';
import {
  THEME_ANALYTICS_FILTER,
  THEME_ANALYTICS_SEGMENT_FILTER,
} from 'modules/insights/constants/constants-theme-analytics-filters';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  ALIGNMENT_ROUTES,
  ANALYZE_ROUTES,
  COACH_ROUTES,
  INSIGHTS_ROUTES,
  INTERACTIONS_ROUTES,
  MY_IMPACT_MODULE_ROUTES,
  REPORTS_ROUTES,
} from 'routes/routes.const';
import {
  TEAM_PROFILE_DETAIL_FILTER,
  TEAM_PROFILE_DETAIL_SEGMENT_FILTER,
} from '../../modules/insights/constants/constants-team-profile-detail-filters';
import useClaims from './useClaims';
import { useInsightsTeams } from 'modules/insights/hooks/useInsightsTeams';
import { useCustomFilters } from './filters';
import {
  FILTER_ITEMS,
  UNIVERSAL_FILTER_ATTRIBUTE_TYPES,
} from 'modules/insights/constants/constants-filter-configs';
import { FilterAttributeType } from 'common/models/page-config.models';
import { RootState } from 'common/store';
import { selectDisplayFilters } from 'common/store/slices/filter-builder.slice';
import { useCompareUrlWithProjectId } from './route';
import { convertAttributeKeyToObjectString } from 'common/utils/filter.utils';
import { REPORT_FILTERS } from '../../modules/insights/constants/constants-report-filters';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import useFeatureFlags from './useFeatureFlags';
import { PLANNING_ROUTES } from 'routes/routes.planning.const';
import { PLANNING_FILTER } from 'modules/insights/constants/constants-planning-filters';

export const PAGE_FILTER_MAP: { [key: string]: FilterConfig[] } = {
  [ALIGNMENT_ROUTES.summary]: ALIGNMENT_FILTER,
  [ALIGNMENT_ROUTES.detail]: ALIGNMENT_DETAIL_FILTER,
  [ALIGNMENT_ROUTES.compare]: ALIGNMENT_FILTER,
  [ANALYZE_ROUTES.elsieai]: ELSIE_AI_FILTER,
  [ANALYZE_ROUTES.elsieaiV2]: ELSIE_AI_FILTER, // TODO: for merging purpuse
  [INSIGHTS_ROUTES.summary]: INSIGHT_SUMMARY_FILTER,
  [INSIGHTS_ROUTES.detail]: INSIGHT_DETAIL_FILTER,
  [INSIGHTS_ROUTES.compare]: INSIGHT_COMPARE_FILTER,
  [INTERACTIONS_ROUTES.root]: INTERACTION_FILTER,
  [ANALYZE_ROUTES.mir]: MIR_FILTER,
  [ANALYZE_ROUTES.themeAnalytics]: THEME_ANALYTICS_FILTER,
  [COACH_ROUTES.summary]: COACH_FILTER,
  [COACH_ROUTES.detail]: COACH_FILTER,
  [COACH_ROUTES.team_detail_interaction_kpis]: COACH_TEAM_MEMBER_DETAILS_FILTER,
  [COACH_ROUTES.team_detail_collected_responses]: COACH_TEAM_MEMBER_DETAILS_FILTER,
  [MY_IMPACT_MODULE_ROUTES.interaction_kpis]: TEAM_PROFILE_DETAIL_FILTER,
  [MY_IMPACT_MODULE_ROUTES.collected_responses]: TEAM_PROFILE_DETAIL_FILTER,
  [REPORTS_ROUTES.view]: REPORT_FILTERS,
  [REPORTS_ROUTES.editor]: REPORT_FILTERS,
  [PLANNING_ROUTES.profiles]: PLANNING_FILTER,
};

export const PAGE_SEGMENT_FILTER_MAP: { [key: string]: FilterConfig[] } = {
  [ALIGNMENT_ROUTES.summary]: ALIGNMENT_SUMMARY_SEGMENT_FILTER,
  [ALIGNMENT_ROUTES.detail]: ALIGNMENT_DETAIL_SEGMENT_FILTER,
  [ALIGNMENT_ROUTES.compare]: ALIGNMENT_DETAIL_SEGMENT_FILTER,
  [INSIGHTS_ROUTES.summary]: INSIGHT_SEGMENT_FILTER,
  [INSIGHTS_ROUTES.detail]: INSIGHT_SEGMENT_FILTER,
  [INSIGHTS_ROUTES.compare]: INSIGHT_SEGMENT_FILTER,
  [INTERACTIONS_ROUTES.root]: INTERACTION_SEGMENT_FILTER,
  [ANALYZE_ROUTES.mir]: MIR_FILTER,
  [ANALYZE_ROUTES.themeAnalytics]: [...THEME_ANALYTICS_FILTER, ...THEME_ANALYTICS_SEGMENT_FILTER],
  [COACH_ROUTES.summary]: COACH_SEGMENT_FILTER,
  [COACH_ROUTES.detail]: COACH_SEGMENT_FILTER,

  [COACH_ROUTES.team_detail_interaction_kpis]: COACH_SEGMENT_FILTER,
  [COACH_ROUTES.team_detail_collected_responses]: COACH_SEGMENT_FILTER,
  [MY_IMPACT_MODULE_ROUTES.interaction_kpis]: [
    ...ALIGNMENT_SUMMARY_SEGMENT_FILTER,
    ...INSIGHT_SEGMENT_FILTER,
    ...TEAM_PROFILE_DETAIL_SEGMENT_FILTER,
    FILTER_ITEMS.categoryStatus,
  ],
  [MY_IMPACT_MODULE_ROUTES.collected_responses]: [
    ...ALIGNMENT_SUMMARY_SEGMENT_FILTER,
    ...INSIGHT_SEGMENT_FILTER,
    ...TEAM_PROFILE_DETAIL_SEGMENT_FILTER,
    FILTER_ITEMS.categoryStatus,
  ],
};

export const PAGE_FILTER_PARENT_MAP: { [key: string]: FilterParent } = {
  [ALIGNMENT_ROUTES.summary]: FilterParent.ALIGNMENT,
  [ALIGNMENT_ROUTES.detail]: FilterParent.ALIGNMENT,
  [ALIGNMENT_ROUTES.compare]: FilterParent.ALIGNMENT,
  [ANALYZE_ROUTES.elsieai]: FilterParent.ACTIVITY_RESPONSES,
  [ANALYZE_ROUTES.elsieaiV2]: FilterParent.ACTIVITY_RESPONSES, // TODO: for merging purpuse
  [INSIGHTS_ROUTES.summary]: FilterParent.SURVEY,
  [INSIGHTS_ROUTES.detail]: FilterParent.SURVEY,
  [INSIGHTS_ROUTES.compare]: FilterParent.SURVEY,
  [INTERACTIONS_ROUTES.root]: FilterParent.INTERACTION,
  [ANALYZE_ROUTES.mir]: FilterParent.MIR,
  [ANALYZE_ROUTES.themeAnalytics]: FilterParent.ACTIVITY_RESPONSES,
  [COACH_ROUTES.summary]: FilterParent.COACH,
  [COACH_ROUTES.detail]: FilterParent.COACH,
  [COACH_ROUTES.team_detail_interaction_kpis]: FilterParent.COACH,
  [COACH_ROUTES.team_detail_collected_responses]: FilterParent.COACH,
  [MY_IMPACT_MODULE_ROUTES.interaction_kpis]: FilterParent.SURVEY,
  [MY_IMPACT_MODULE_ROUTES.collected_responses]: FilterParent.SURVEY,
};

interface UsePageFiltersArgs {
  datasetId: string;
  applyEffects?: boolean;
}

export interface UsePageFilters {
  pageFilters: FilterConfig[];
  customFilters: FilterConfig[];
  pageFilterKeys: Array<CommonFilterList | string>;
  currentPageFilterParent: FilterParent;
}

function usePageFilters(
  { datasetId }: UsePageFiltersArgs = { datasetId: DATASET_INITIAL_ID }
): UsePageFilters {
  const isUniversalFilters = datasetId?.includes('universal-');
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { projectId } = useParams();
  const { hasClaim } = useClaims();
  const { hasFeature } = useFeatureFlags();
  const insightsTeams = useInsightsTeams();
  const rawCustomFilters = useCustomFilters();
  const { activeProject } = useModuleProjectContext();
  const storedFilterConfig = useSelector((state: RootState) =>
    selectDisplayFilters(state, datasetId)
  );
  const storedFilterParent = useSelector(selectFilterParent);
  const { compareUrl } = useCompareUrlWithProjectId();
  const filterExclusions = useMemo(
    () => CLIENT_FILTER_EXCLUSIONS[activeProject?.projectClientName || ''] ?? [],
    [activeProject]
  );

  const sanitizedUrl = pathname.replace(`${projectId}`, ':projectId').replace(/\/\d+$/, '');
  const currentPageFilterParent: FilterParent = PAGE_FILTER_PARENT_MAP[sanitizedUrl];

  // Set appropriate parent filter
  useEffect(() => {
    if (
      currentPageFilterParent !== storedFilterParent &&
      !compareUrl(REPORTS_ROUTES.root, { includes: true })
    ) {
      dispatch(setFilterParent(currentPageFilterParent));
    }
  }, [currentPageFilterParent, dispatch, storedFilterParent, compareUrl]);

  const customFilters = useMemo(
    () =>
      rawCustomFilters?.filter((filter) => {
        if (isUniversalFilters) {
          return (
            UNIVERSAL_FILTER_ATTRIBUTE_TYPES.includes(
              filter.filterAttributeType as FilterAttributeType
            ) && hasClaim(filter.claims)
          );
        }

        return hasClaim(filter.claims);
      }) ?? [],
    [rawCustomFilters, hasClaim, isUniversalFilters]
  );

  const pageFilters = useMemo(
    () =>
      (PAGE_FILTER_MAP[sanitizedUrl] ?? storedFilterConfig ?? []).filter((filter) => {
        // Check if the user has correct claim and project feature for the filter
        if (!hasClaim(filter.claims) || !hasFeature(filter.features)) {
          return false;
        }

        // Check that the filter isn't on the exclusion list
        if (filterExclusions.includes(filter.property)) {
          return false;
        }

        // Check that the Team filter should show
        if (
          (filter.property as CommonFilterList) === CommonFilterList.Team &&
          (!insightsTeams.teams || insightsTeams.teams.length === 0)
        ) {
          return false;
        }

        return true;
      }),
    [sanitizedUrl, storedFilterConfig, hasClaim, hasFeature, filterExclusions, insightsTeams.teams]
  );

  const pageSegmentFilters = useMemo(
    () =>
      (PAGE_SEGMENT_FILTER_MAP[sanitizedUrl] ?? []).filter((filter) => {
        // Check if the user has correct claim for the filter
        if (!hasClaim(filter.claims)) {
          return false;
        }

        // Check that the filter isn't on the exclusion list
        if (filterExclusions.includes(filter.property)) {
          return false;
        }

        // Check that the Team filter should show
        if (
          (filter.property as CommonFilterList) === CommonFilterList.Team &&
          (!insightsTeams.teams || insightsTeams.teams.length === 0)
        ) {
          return false;
        }

        return true;
      }),
    [filterExclusions, hasClaim, insightsTeams.teams, sanitizedUrl]
  );

  const pageFilterKeys: Array<CommonFilterList | string> = useMemo(
    () =>
      [
        ...pageFilters,
        ...pageSegmentFilters,
        ...customFilters.map((filter) => ({
          ...filter,
          // property needs updating because the names are different format in the filters
          property: convertAttributeKeyToObjectString(filter.property),
        })),
        ...customFilters,

        // HardCoded Page Config Level Filters
        {
          property: 'ResponseDetail',
        },
        {
          property: 'DiscussionId',
        },
      ].map((filter) => filter.property),
    [customFilters, pageFilters, pageSegmentFilters]
  );

  if (pageFilterKeys.includes(CommonFilterList.Survey)) {
    pageFilterKeys.push(CommonFilterList.Question);
    pageFilterKeys.push(CommonFilterList.Response);
  }

  return useMemo(
    () => ({
      pageFilters,
      customFilters,
      pageFilterKeys,
      currentPageFilterParent,
    }),
    [currentPageFilterParent, customFilters, pageFilterKeys, pageFilters]
  );
}

export default usePageFilters;
